import { isEmpty } from 'lodash';

import type { InvalidateOptions } from '@tanstack/query-core';

/**
 * Centralized query keys for presenter composables based on TanStack Query queries.
 * Used for cache invalidation and query deduplication.
 */
export const DEFAULT_INVALIDATE_OPTIONS: InvalidateOptions = {
  cancelRefetch: false
};

export const queryKeys = {
  all: ['yard'] as const,
  carrierAll: ['carrier'] as const,
  carrier: (carrierId: string) => [...queryKeys.carrierAll, carrierId] as const,
  carrierLookup: (lookup: { carrierId?: string | null; companyId?: string | null }) =>
    [...queryKeys.carrierAll, 'lookup', lookup] as const,

  docks: ['docks'] as const,
  dock: (dockId: string) => [...queryKeys.docks, dockId] as const,

  // Warehouses
  warehouses: () => [...queryKeys.all, 'warehouses'] as const,
  /** List cache is split by restriction fingerprint (`all` or sorted joined ids). */
  warehousesList: (restrictionFingerprint = 'all') =>
    [...queryKeys.warehouses(), 'list', restrictionFingerprint] as const,
  warehouse: (warehouseId: string) => [...queryKeys.warehouses(), warehouseId] as const,

  // Spots
  spots: () => [...queryKeys.all, 'spots'] as const,
  spotsList: (warehouseId: string) => [...queryKeys.spots(), 'list', warehouseId] as const,

  spot: (spotId: string) => [...queryKeys.all, 'spot', spotId] as const,
  spotDetail: (spotId: string) => [...queryKeys.spot(spotId), 'detail'] as const,
  spotSummary: (spotId: string) => [...queryKeys.spot(spotId), 'summary'] as const,
  spotEvents: (spotId: string) => [...queryKeys.spot(spotId), 'events'] as const,

  // Spot Assignments
  spotAssignments: (spotId: string) => [...queryKeys.spot(spotId), 'assignments'] as const,
  spotAssignment: (assignmentId: string) =>
    [...queryKeys.all, 'spotAssignment', assignmentId] as const,

  // Asset Visits
  assetVisits: () => [...queryKeys.all, 'assetVisits'] as const,
  assetVisitsList: (warehouseId: string) =>
    [...queryKeys.assetVisits(), 'list', warehouseId] as const,
  waitingAssetsList: (warehouseId: string) =>
    [...queryKeys.assetVisits(), 'waiting', warehouseId] as const,
  assetVisit: (visitId: string) => [...queryKeys.assetVisits(), visitId] as const,

  // Asset Containers
  assetContainers: () => [...queryKeys.all, 'assetContainers'] as const,
  assetContainer: (containerId: string) => [...queryKeys.assetContainers(), containerId] as const,

  // Missing assets list (BFF: standalone containers whose latest status is Missing)
  missingAssetsList: (warehouseId: string) =>
    [...queryKeys.assetContainers(), 'missing', warehouseId] as const,

  // Yard inventory check (the "open inventory" BFF, keyed by the task it resolves)
  yardInventoryChecks: () => [...queryKeys.assetContainers(), 'yardInventoryCheck'] as const,
  yardInventoryCheck: (taskId: string) => [...queryKeys.yardInventoryChecks(), taskId] as const,

  // Yard inventory counts (warehouse-scoped container counts for the check-task picker + cards)
  yardInventoryCounts: (warehouseId: string) =>
    [...queryKeys.assetContainers(), 'yardInventoryCounts', warehouseId] as const,

  // Yard Tasks
  task: (taskId: string) => [...queryKeys.all, 'task', taskId] as const,
  tasksByRelatedEntityList: (
    relatedEntity: string,
    relatedIdsFingerprint: string,
    statusesFingerprint: string,
    type: string
  ) =>
    [
      ...queryKeys.all,
      'tasks',
      'byRelatedEntity',
      relatedEntity,
      relatedIdsFingerprint,
      statusesFingerprint,
      type
    ] as const,
  tasksByStatusList: (status: string, warehouseFingerprint: string, filtersFingerprint: string) =>
    [
      ...queryKeys.all,
      'tasks',
      'byStatus',
      status,
      warehouseFingerprint,
      filtersFingerprint
    ] as const,

  // Appointments
  appointment: (appointmentId: string) => [...queryKeys.all, 'appointment', appointmentId] as const,

  // Load types
  loadType: (loadTypeId: string) => [...queryKeys.all, 'loadType', loadTypeId] as const,

  // Spot Areas (Yard Areas)
  spotAreas: () => [...queryKeys.all, 'spotAreas'] as const,
  spotAreasList: (warehouseId: string) => [...queryKeys.spotAreas(), 'list', warehouseId] as const,
  spotArea: (areaId: string) => [...queryKeys.all, 'spotArea', areaId] as const,
  spotAreaDetail: (areaId: string) => [...queryKeys.spotArea(areaId), 'detail'] as const,

  // BFF View endpoints (pre-computed, flat data for specific views)
  bff: () => [...queryKeys.all, 'bff'] as const,
  listViewRows: (warehouseId: string, includeWaiting = true) =>
    [...queryKeys.bff(), 'listViewRows', warehouseId, includeWaiting] as const,
  spotGrid: (warehouseId: string) => [...queryKeys.bff(), 'spotGrid', warehouseId] as const,

  /** Custom form data presenter cache (object + trigger-set fingerprint). */
  customFormDataPresenter: (objectId: string, triggerIdsFingerprint: string) =>
    [...queryKeys.all, 'customFormData', 'presenter', objectId, triggerIdsFingerprint] as const
} as const;

export const taskKeys = {
  all: ['tasks'] as const,
  list: (queryArgs?: unknown) => {
    return [...taskKeys.all, 'list', ...(isEmpty(queryArgs) ? [] : [queryArgs])] as const;
  },
  detail: (taskId: string) => {
    return [...taskKeys.all, 'detail', taskId] as const;
  },
  activeMoveTasks: (params?: {
    relatedEntity?: string;
    relatedIds?: string;
    statuses?: string;
    type?: string;
  }) => {
    return [...taskKeys.all, 'active-move-tasks', ...(isEmpty(params) ? [] : [params])] as const;
  }
};
